import React, { useState } from 'react';
import fr from 'facet-rest';
import moment from 'moment';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { Tooltip, Typography } from '@material-ui/core';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import ConfirmationDialog from 'Components/UI/ConfirmationDialog';
import ReminderScheduleSendReview from 'Components/Modals/ScheduleSendReview/ReminderScheduleSendReview';

const styles = theme => ({
  root: {
    display: 'flex',
    cursor: 'pointer',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(0.5),
    boxShadow: '0px 3px 15px #0000000B',
    flexDirection: 'row',
    justifyContent: 'space-between',
    '&:hover': {
      textDecoration: 'none',
      color: 'inherit',
    },
    padding: '3px 0px',
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemAccent: {
    width: '5px',
    height: '100%',
    margin: '3px 8px 3px 3px',
    backgroundColor: theme.palette.facet.accentBlue,
  },
  itemText: {
    color: theme.palette.facet.primaryGray,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dateText: {
    color: theme.palette.facet.accentBlue2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  reminderButton: {
    padding: theme.spacing(0, 1),
  },
  feedback: {
    display: 'flex',
    marginLeft: theme.spacing(0.5),
    alignItems: 'center',
  },
  feedbackButton: {
    padding: theme.spacing(0.5),
    height: 30,
    width: 30,
  },
  complete: {
    color: theme.palette.facet.accentGreen,
  },
  cancel: {
    color: theme.palette.facet.red,
  },
  reminderText: {
    display: 'flex',
    flexDirection: 'column',
  },
  reviewed: {
    color: theme.palette.secondary.main,
    padding: theme.spacing(1),
  },
});

const ScheduleSendReminder = ({
  classes,
  reminder,
  refreshReminders,
}) => {
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const [reviewScheduleSendModalOpen, setReviewScheduleSendModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dueDateMoment = moment(reminder.attr('dueDate'));
  const isReviewed = reminder.attr('completed') === 'true';

  const handleReminderDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    const meetingAgendaResponse = await reminder.relationship(fr.relationships.meetingAgenda.name).link('related').get();
    await meetingAgendaResponse.resources().head().delete();
    refreshReminders();
    setLoading(false);
    setDeleteConfirmationModalOpen(false);
  };
  return (
    <>
      <div
        className={classes.root}
        onClick={() => {
          setReviewScheduleSendModalOpen(true);
        }}
        onKeyPress={() => setReviewScheduleSendModalOpen(true)}
        tabIndex={0}
        role="button"
      >
        <div className={classes.itemContainer}>
          <div className={classes.itemAccent} />
          <div className={classes.reminderText}>
            <Typography variant="h6" className={classes.itemText}>{reminder.attr('name')}</Typography>
            <Typography variant="h6" className={classes.itemText}>{reminder.attr('description')}</Typography>
            <Typography variant="subtitle2" className={classes.dateText}>{`${dueDateMoment.format('LL')} at ${dueDateMoment.format('LT')}`}</Typography>
          </div>
        </div>
        <div className={classes.feedback}>
          {isReviewed && <Typography className={classes.reviewed}>Reviewed</Typography>}
          {!isReviewed && (
            <>
              <Tooltip title="Review Scheduled Send" placement="top" arrow>
                <IconButton
                  className={classNames(classes.complete, classes.feedbackButton)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setReviewScheduleSendModalOpen(true);
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Scheduled Send" placement="top" arrow>
                <IconButton
                  className={classNames(classes.cancel, classes.feedbackButton)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirmationModalOpen(true);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      {deleteConfirmationModalOpen && (
        <ConfirmationDialog
          onCancel={() => {
            setDeleteConfirmationModalOpen(false);
          }}
          title="Are you sure?"
          message="This will delete this scheduled email and will not be sent."
          onConfirm={handleReminderDelete}
          showCloseIcon={false}
          loading={loading}
        />
      )}
      {reviewScheduleSendModalOpen && (
        <ReminderScheduleSendReview
          reminderResource={reminder}
          isOpen={reviewScheduleSendModalOpen}
          closeModal={() => setReviewScheduleSendModalOpen(false)}
          refreshResources={refreshReminders}
        />
      )}
    </>
  );
};

ScheduleSendReminder.propTypes = {
  classes: PropTypes.object.isRequired,
  reminder: PropTypes.object.isRequired,
  refreshReminders: PropTypes.func.isRequired,
};

export default withStyles(styles)(ScheduleSendReminder);
